<template>
  <v-container fluid grid-list-md>
    <!-- intro ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
    <CorePageIntro>
      <template slot="content">
        The report summarizes run event performances for the selected date
        range. <strong>All data presented are averages.</strong>
      </template>
    </CorePageIntro>

    <v-card outlined>

      <!-- section header ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
        <v-toolbar flat>
            <v-toolbar-title class="text-uppercase body-1 font-weight-medium">Summary</v-toolbar-title>
            <v-divider class="mx-4" inset vertical></v-divider>

            <v-col cols="5" sm="5" md="5">
                  Date Range:
                  <date-picker
                    v-model="rundate"
                    :shortcuts="shortcuts"
                    :not-after="rundateUpper"
                    clearable
                    range
                    confirm
                    width="330"
                    lang="en"
                    type="date"
                    format="YYYY-MM-DD"
                  >
                  </date-picker>
                  <v-btn @click="fetch()" class="mx-2" fab dark small color="orange darken-3">
                    <v-icon dark>sync</v-icon>
                  </v-btn>
            </v-col>

            <div class="flex-grow-1"></div>

            <!-- search ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
            <v-col cols="3" sm="3" md="3">
              <v-text-field
                  v-model="search"
                  append-icon="search"
                  label="Search"
                  single-line
                  hide-details
                  ></v-text-field>
            </v-col>
          </v-toolbar>

          <!-- table ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
          <v-data-table
            :headers="headers"
            :items="tableData"
            :search="search"
            :sort-by="['date', 'ending']"
            :sort-desc="[false, false]"
            :items-per-page="15"
            item-key="ending"
            multi-sort
            dense
          >

            <!-- pre-header spacing ~*~*~*~*~*~*~*~*~*~*~*~* -->
            <template v-slot:top>
              <p>&nbsp;</p>
            </template>

            <!-- no-data ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
            <template slot="no-data" class="text-left">
              <span class="subheading text-left deeporange--text">
                <v-icon color="deep-orange">warning</v-icon>
                No reports found. Please select a different date range.
              </span>
            </template>

            <!-- content  ~*~*~*~*~*~*~*~* -->
            <template v-slot:item.date="{ item }">
              {{ item.date }}
            </template>
            <template v-slot:item.beginning="{ item }">
              {{ item.beginning }}
            </template>
            <template v-slot:item.ending="{ item }">
              {{ item.ending }}
            </template>
            <template v-slot:item.ending="{ item }">
              {{ item.ending | $_datetimeFilter_timestamp }}
            </template>
            <template v-slot:item.avgTargetOutputKw="{ item }">
              {{ item.avgTargetOutputKw | $_powerFilter_kw }}
            </template>
            <template v-slot:item.avgActualOutputKw="{ item }">
              {{ item.avgActualOutputKw | $_powerFilter_kw }}
            </template>
            <template v-slot:item.avgValueAtStake="{ item }">
              {{ item.avgValueAtStake | $_currencyFilter_usd }}
            </template>
            <template v-slot:item.avgPerformance="{ item }">
              <span v-bind:style="{color: statColor(item.avgPerformance)}">
                {{ item.avgPerformance }}%
              </span>
            </template>
            <template v-slot:item.programNames="{ item }">
              <span v-html="item.programNames" class="caption"></span>              
            </template>
            <template v-slot:item.programNames="{ item }">
              <span v-html="item.programNames" class="caption"></span>              
            </template>

            <template v-slot:item.href="{ item }">
                  <v-tooltip left color="primary">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        :to="item.href"
                        color="blue"
                        dark
                        elevation-4
                        icon
                        outlined
                        small
                      >
                        <v-icon medium color="primary">more_horiz</v-icon>
                      </v-btn>
                    </template>
                    <span class="white--text font-weight-bold body-2">
                      See Report</span>
                  </v-tooltip>
              </template>
          </v-data-table>
        </v-card>
  </v-container>
</template>

<script>
import moment from 'moment'
import FacilityService from '@/services/FacilityService'
import { mapGetters } from 'vuex'
export default {
  name: 'PerformanceSummary',
  data() {
    return {
      search: '',
      pagination: {
        rowsPerPage: 10,
        sortBy: 'date'
      },
      // date range picker
      rundate: [],
      rundateUpper: '',
      shortcuts: [
        {
          text: 'Yesterday',
          onClick: () => {
            this.rundate = [
              moment()
                .subtract(1, 'days')
                .toDate(),
              moment()
                .subtract(1, 'days')
                .toDate()
            ]
          }
        },
        {
          text: 'This Month',
          onClick: () => {
            this.rundate = [
              moment()
                .startOf('month')
                .toDate(),
              moment()
                .endOf('month')
                .toDate()
            ]
          }
        },
        {
          text: 'Last Month',
          onClick: () => {
            this.rundate = [
              moment()
                .subtract(1, 'months')
                .startOf('month')
                .toDate(),
              moment()
                .subtract(1, 'months')
                .endOf('month')
                .toDate()
            ]
          }
        },
        {
          text: 'Past 30 Days',
          onClick: () => {
            this.rundate = [
              moment()
                .subtract(31, 'days')
                .toDate(),
              moment()
                .subtract(1, 'days')
                .toDate()
            ]
          }
        }
      ],
      dataError: {
        code: '',
        message: ''
      },
      rawData: '',
      tableData: [],
      headers: [
        // {
        //   text: 'Report Id',
        //   align: 'right',
        //   sortable: false,
        //   value: 'guidanceReportId'
        // },
        {
          text: 'Date',
          align: 'right',
          sortable: true,
          value: 'date'
        },
        {
          text: 'Begin',
          align: 'right',
          sortable: true,
          value: 'beginning'
        },
        {
          text: 'End',
          align: 'right',
          sortable: true,
          value: 'ending'
        },
        {
          text: 'Target Output',
          align: 'right',
          sortable: true,
          value: 'avgTargetOutputKw'
        },
        {
          text: 'Actual Output',
          align: 'avgTargetOutputKw',
          sortable: true,
          value: 'avgActualOutputKw'
        },
        {
          text: 'Value',
          align: 'right',
          sortable: true,
          value: 'avgValueAtStake'
        },
        {
          text: 'Performance',
          align: 'center',
          sortable: true,
          value: 'performance'
        },
        {
          text: 'Programs',
          align: 'right',
          sortable: true,
          value: 'numPrograms'
        },
        {
          text: '',
          align: 'left',
          sortable: false,
          value: 'href'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['getFacilityId', 'getFacilityTimezone'])
  },
  created() {
    // default report time is last month
    this.rundate = [
      moment()
        .subtract(1, 'months')
        .startOf('month')
        .toDate(),
      moment()
        .subtract(1, 'months')
        .endOf('month')
        .hour(23)
        .minute(45)
        .toDate()
    ]
    this.rundateUpper = moment().subtract(1, 'days')

    // start
    this.fetch()
  },
  methods: {
    fetch() {
      let s = this.$_datetimeMethod_formatDate(this.rundate[0])
      let e = this.$_datetimeMethod_formatDate(this.rundate[1])
      // this.rawData = MockDataService.getPerformanceSummary()
      // this.parseData()
      FacilityService.getPerformanceSummary(this.getFacilityId, s, e)
        .then(response => {
          this.rawData = response.data
          this.parseData()
        })
        .catch(error => { this.$toast.error("Error loading data; " + error.message) })
    },
    parseData() {
      this.tableData = []

      let rowKey = 0
      let programKey = 0

      for (var r = 0; r < this.rawData.run_events.length; r++) {
        let runEvent = this.rawData.run_events[r]
        // console.log("run_event", runEvent)
        let href =
          '/report/performance/' +
          this.$_datetimeMethod_formatTimeInIso(
            runEvent.ending,
            this.getFacilityTimezone
          )
        let programs = []
        let programNames = ''
        let numUniquePrograms = 0

        for (var p = 0; p < runEvent.programs.length; p++) {
          let prog = runEvent.programs[p]
          let myprog = {
            key: rowKey++,
            programId: prog.program_id,
            description: prog.description,
            valueAtStake: parseFloat(prog.value_at_stake).toFixed(2),
            guidance: prog.guidance,
            programKw: prog.program_kw,
            programPriceMw: prog.program_price_mw,
            performance: parseInt(prog.performance * 100.0)
          }
          programs.push(myprog)

          if (programNames.indexOf(prog.description) === -1) {
            programNames += '<nobr>' + prog.description + '</nobr><br/>'
            numUniquePrograms++
          }
        }

        let row = {
          key: programKey++,
          href: href,
          guidanceReportId: runEvent.guidance_report_id,
          date: this.$_datetimeMethod_formatDate(runEvent.beginning),
          beginning: this.$_datetimeMethod_formatTimeHoursMinutes(
            runEvent.beginning
          ),
          ending: this.$_datetimeMethod_formatTimeHoursMinutes(runEvent.ending),
          totalLoadForecastKw: parseInt(runEvent.total_load_forecast_kw),
          totalTargetOutputKw: parseInt(runEvent.total_target_output_kw),
          totalActualOutputKw: parseInt(runEvent.total_actual_output_kw),
          totalValueAtStake: parseFloat(runEvent.total_value_at_stake).toFixed(
            2
          ),
          totalPerformance: parseInt(runEvent.total_performance * 100),
          avgLoadForecastKw: parseInt(runEvent.avg_load_forecast_kw),
          avgTargetOutputKw: parseInt(runEvent.avg_target_output_kw),
          avgActualOutputKw: parseInt(runEvent.avg_actual_output_kw),
          avgValueAtStake: parseFloat(runEvent.avg_value_at_stake).toFixed(2),
          avgPerformance: parseInt(runEvent.avg_performance * 100),

          numPrograms: programs.length,
          programs: programs,
          programNames: programNames,
          numUniquePrograms: numUniquePrograms
        }
        this.tableData.push(row)
      }
    },

    statColor(value) {
      if (value < 90) {
        return 'red'
      }
      if (value < 100) {
        return 'amber darken-1'
      }
      return 'green'
    },
    programList(array) {
      let retVal = ''
      for (var p = 0; p < array.length; p++) {
        retVal += '<nobr>' + array[p].description + '</nobr><br/>'
      }
      return retVal
    }
  }
}
</script>
